import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, Search, TrendingUp, TrendingDown, BarChart2, ChevronDown, Info } from 'lucide-react';
import Card from '../components/Card';
import PriceChange from '../components/PriceChange';
import LoadingSpinner from '../components/LoadingSpinner';
import AIAnalysis from '../components/AIAnalysis';
import PriceChart from '../components/PriceChart';
import eodhd from '../services/eodhd';
import { useHotMovers } from '../hooks/useMarketData';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import { useQuery } from '@tanstack/react-query';
import { expertApi } from '../services/expertApi';
import TradingViewAdvancedChart from '../components/TradingViewAdvancedChart';

interface TechnicalIndicator {
  name: string;
  value: number;
  signal: 'buy' | 'sell' | 'neutral';
  description?: string;
}

interface IndicatorDetails {
  rsi: number | null;
  macd: { macd: number; signal: number; histogram: number } | null;
  sma50: number | null;
  sma200: number | null;
  ema20: number | null;
  bollingerBands: { upper: number; middle: number; lower: number } | null;
  stochastic: { k: number; d: number } | null;
  atr: number | null;
  adx: number | null;
}

const Analysis: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlSymbol = searchParams.get('symbol');

  // Load hot movers to populate dropdown
  const { data: hotMoversData } = useHotMovers();
  const hotMovers = hotMoversData || { gainers: [], losers: [] };

  // Build symbol list from hot movers
  const popularSymbols = React.useMemo(() => {
    const symbols: Array<{ symbol: string; name: string }> = [];

    // Add gainers (symbols already include exchange suffix like "NPN.JSE")
    hotMovers.gainers.forEach(mover => {
      symbols.push({ symbol: mover.symbol, name: mover.name });
    });

    // Add losers (symbols already include exchange suffix)
    hotMovers.losers.forEach(mover => {
      symbols.push({ symbol: mover.symbol, name: mover.name });
    });

    // Fallback to default symbols if hot movers not loaded yet
    if (symbols.length === 0) {
      return [
        { symbol: 'NPN.JSE', name: 'Naspers' },
        { symbol: 'SOL.JSE', name: 'Sasol' },
        { symbol: 'AAPL.US', name: 'Apple Inc.' },
        { symbol: 'MSFT.US', name: 'Microsoft' },
      ];
    }

    return symbols;
  }, [hotMovers]);

  // Get first JSE stock as default
  const getDefaultSymbol = () => {
    const firstJSE = popularSymbols.find(s => s.symbol.endsWith('.JSE'));
    return firstJSE?.symbol || 'NPN.JSE';
  };

  const initialSymbol = urlSymbol || getDefaultSymbol();

  const [searchSymbol, setSearchSymbol] = useState(initialSymbol);
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol);
  const [loading, setLoading] = useState(true);
  const [priceData, setPriceData] = useState<{ price: number; change: number; previousClose?: number }>({ price: 0, change: 0 });

  // WebSocket real-time price (for the symbol info header)
  const livePrice = useRealtimePrice(currentSymbol);

  const [technicalIndicators, setTechnicalIndicators] = useState<TechnicalIndicator[]>([]);
  const [_indicatorDetails, setIndicatorDetails] = useState<IndicatorDetails | null>(null);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [overallSignal, setOverallSignal] = useState<'BULLISH' | 'BEARISH' | 'NEUTRAL'>('NEUTRAL');
  const [confidenceScore, setConfidenceScore] = useState(50);

  const [showDropdown, setShowDropdown] = useState(false);

  // Chart provider preference (v30-1-6)
  const { data: bankerPrefs } = useQuery({
    queryKey: ['bankerPreferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
  });

  // Update symbol when URL parameter changes
  useEffect(() => {
    if (urlSymbol && urlSymbol !== currentSymbol) {
      setSearchSymbol(urlSymbol);
      setCurrentSymbol(urlSymbol);
    }
  }, [urlSymbol, currentSymbol]);

  // Load price + indicators on symbol change
  useEffect(() => {
    loadSymbolData(currentSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSymbol]);

  // Load price data and technical indicators when symbol changes
  const loadSymbolData = async (sym: string) => {
    try {
      setLoading(true);
      const currentPrice = await eodhd.getRealTimePrice(sym);
      setPriceData({
        price: currentPrice.close || 0,
        change: currentPrice.change_percent ?? currentPrice.change_p ?? 0,
        previousClose: currentPrice.previous_close,
      });
      await loadTechnicalIndicators(sym, currentPrice.close || 100);
    } catch (error) {
      console.error('[Analysis] Error loading symbol data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicalIndicators = async (symbol: string, currentPrice: number) => {
    setIndicatorsLoading(true);

    try {
      console.log('[Analysis] Fetching all technical indicators from backend...');

      // Fetch all indicators in a single call from our backend API
      // Backend returns all indicators calculated with current IRESS price for JSE stocks
      const response = await eodhd.getTechnicalIndicator(symbol, 'all', { period: 14 });
      console.log('[Analysis] Technical indicators response:', response);

      // Parse the backend response format
      const data = response as {
        symbol: string;
        current_price: number;
        indicators: {
          rsi?: number;
          macd?: { value: number; signal: number; histogram: number };
          moving_averages?: { sma_20?: number; sma_50?: number; sma_200?: number; ema_20?: number };
          bollinger_bands?: { upper: number; middle: number; lower: number };
          volume_ratio?: number;
        };
      };

      // Check if this is a JSE stock (backend returns prices in cents for JSE)
      const isJSE = symbol.endsWith('.JSE');

      // Extract values from the unified response
      const rsi = data.indicators?.rsi ?? null;
      const macd = data.indicators?.macd ? {
        macd: data.indicators.macd.value,
        signal: data.indicators.macd.signal,
        histogram: data.indicators.macd.histogram
      } : null;

      // For JSE stocks, backend returns SMA in cents, convert to ZAR for display/comparison
      let sma50 = data.indicators?.moving_averages?.sma_50 ?? null;
      let sma200 = data.indicators?.moving_averages?.sma_200 ?? null;
      let ema20 = data.indicators?.moving_averages?.ema_20 ?? null;

      if (isJSE) {
        if (sma50 !== null) sma50 = sma50 / 100;
        if (sma200 !== null) sma200 = sma200 / 100;
        if (ema20 !== null) ema20 = ema20 / 100;
      }

      const bollingerBands = data.indicators?.bollinger_bands ?? null;

      // Note: Stochastic, ATR, ADX are not yet implemented in backend
      const stochastic = null;
      const atr = null;
      const adx = null;

      // Use backend's current_price if available (already in cents for JSE)
      if (data.current_price) {
        console.log('[Analysis] Using backend current_price:', data.current_price);
        // For JSE stocks, backend returns price in cents, convert to ZAR for comparison
        currentPrice = isJSE ? data.current_price / 100 : data.current_price;
      }

      setIndicatorDetails({
        rsi,
        macd,
        sma50,
        sma200,
        ema20,
        bollingerBands,
        stochastic,
        atr,
        adx
      });

      // Build indicator list with signals
      const indicators: TechnicalIndicator[] = [];
      let buySignals = 0;
      let sellSignals = 0;

      // RSI Signal
      if (rsi !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (rsi < 30) { signal = 'buy'; buySignals++; }
        else if (rsi > 70) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'RSI (14)',
          value: rsi,
          signal,
          description: rsi < 30 ? 'Oversold - potential buying opportunity' : rsi > 70 ? 'Overbought - potential selling opportunity' : 'Neutral zone'
        });
      }

      // MACD Signal
      if (macd !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (macd.histogram > 0 && macd.macd > macd.signal) { signal = 'buy'; buySignals++; }
        else if (macd.histogram < 0 && macd.macd < macd.signal) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'MACD',
          value: macd.macd,
          signal,
          description: signal === 'buy' ? 'Bullish crossover' : signal === 'sell' ? 'Bearish crossover' : 'No clear signal'
        });
      }

      // SMA 50 Signal (Price vs SMA)
      if (sma50 !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (currentPrice > sma50 * 1.02) { signal = 'buy'; buySignals++; }
        else if (currentPrice < sma50 * 0.98) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'SMA (50)',
          value: sma50,
          signal,
          description: currentPrice > sma50 ? 'Price above 50-day average' : 'Price below 50-day average'
        });
      }

      // SMA 200 Signal (Price vs SMA)
      if (sma200 !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (currentPrice > sma200 * 1.02) { signal = 'buy'; buySignals++; }
        else if (currentPrice < sma200 * 0.98) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'SMA (200)',
          value: sma200,
          signal,
          description: currentPrice > sma200 ? 'Long-term bullish trend' : 'Long-term bearish trend'
        });
      }

      // Golden/Death Cross Signal
      if (sma50 !== null && sma200 !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (sma50 > sma200) { signal = 'buy'; buySignals++; }
        else if (sma50 < sma200) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'Golden/Death Cross',
          value: sma50 - sma200,
          signal,
          description: sma50 > sma200 ? 'Golden Cross - bullish' : 'Death Cross - bearish'
        });
      }

      // EMA 20 Signal
      if (ema20 !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (currentPrice > ema20 * 1.01) { signal = 'buy'; buySignals++; }
        else if (currentPrice < ema20 * 0.99) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'EMA (20)',
          value: ema20,
          signal,
          description: currentPrice > ema20 ? 'Short-term bullish' : 'Short-term bearish'
        });
      }

      // Stochastic Signal
      if (stochastic !== null) {
        let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
        if (stochastic.k < 20 && stochastic.k > stochastic.d) { signal = 'buy'; buySignals++; }
        else if (stochastic.k > 80 && stochastic.k < stochastic.d) { signal = 'sell'; sellSignals++; }
        indicators.push({
          name: 'Stochastic',
          value: stochastic.k,
          signal,
          description: stochastic.k < 20 ? 'Oversold zone' : stochastic.k > 80 ? 'Overbought zone' : 'Neutral zone'
        });
      }

      // ADX Signal (Trend Strength)
      if (adx !== null) {
        indicators.push({
          name: 'ADX (Trend)',
          value: adx,
          signal: 'neutral',
          description: adx > 50 ? 'Very strong trend' : adx > 25 ? 'Strong trend' : 'Weak/no trend'
        });
      }

      // ATR (Volatility)
      if (atr !== null) {
        indicators.push({
          name: 'ATR (Volatility)',
          value: atr,
          signal: 'neutral',
          description: `Average True Range: ${atr.toFixed(2)}`
        });
      }

      setTechnicalIndicators(indicators);

      // Calculate overall signal
      const totalSignals = buySignals + sellSignals;
      const confidence = totalSignals > 0 ? Math.round((Math.max(buySignals, sellSignals) / indicators.length) * 100) : 50;
      setConfidenceScore(confidence);

      if (buySignals > sellSignals + 1) {
        setOverallSignal('BULLISH');
      } else if (sellSignals > buySignals + 1) {
        setOverallSignal('BEARISH');
      } else {
        setOverallSignal('NEUTRAL');
      }

    } catch (error) {
      console.error('[Analysis] Error loading technical indicators:', error);
      // Don't set fallback indicators - leave empty to show error state in UI
      setTechnicalIndicators([]);
      setOverallSignal('NEUTRAL');
      setConfidenceScore(50);
    } finally {
      setIndicatorsLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchSymbol.trim()) {
      setCurrentSymbol(searchSymbol.toUpperCase());
      setShowDropdown(false);
    }
  };

  const handleSelectSymbol = (symbol: string) => {
    setSearchSymbol(symbol);
    setCurrentSymbol(symbol);
    setShowDropdown(false);
  };

  const getSignalColor = (signal: 'buy' | 'sell' | 'neutral') => {
    switch (signal) {
      case 'buy':
        return 'text-emerald-400';
      case 'sell':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getSignalBg = (signal: 'buy' | 'sell' | 'neutral') => {
    switch (signal) {
      case 'buy':
        return 'bg-emerald-500/20';
      case 'sell':
        return 'bg-red-500/20';
      default:
        return 'bg-slate-500/20';
    }
  };

  const getCurrencySymbol = (symbol: string) => {
    if (symbol.endsWith('.JSE')) {
      return 'R';
    }
    if (symbol.endsWith('.FOREX')) {
      return '';
    }
    return '$';
  };

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="text-teal-400" />
            Technical Analysis
          </h1>
          <p className="text-sm text-slate-400 mt-1">Advanced charting and technical indicators</p>
        </div>

        {/* Symbol Search */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Dropdown for popular symbols */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
            >
              <span>Quick Select</span>
              <ChevronDown size={16} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showDropdown && (
              <div className="absolute top-full mt-1 right-0 w-72 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                <div className="p-2 border-b border-slate-700">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">US Stocks</p>
                </div>
                {popularSymbols.filter(s => s.symbol.endsWith('.US')).map((item) => (
                  <button
                    key={item.symbol}
                    onClick={() => handleSelectSymbol(item.symbol)}
                    className={`w-full text-left px-4 py-2 hover:bg-slate-700 transition-colors flex items-center justify-between ${
                      currentSymbol === item.symbol ? 'bg-teal-500/20 text-teal-400' : ''
                    }`}
                  >
                    <span className="font-medium">{item.symbol.split('.')[0]}</span>
                    <span className="text-xs text-slate-400">{item.name}</span>
                  </button>
                ))}
                <div className="p-2 border-y border-slate-700">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">JSE (South Africa)</p>
                </div>
                {popularSymbols.filter(s => s.symbol.endsWith('.JSE')).map((item) => (
                  <button
                    key={item.symbol}
                    onClick={() => handleSelectSymbol(item.symbol)}
                    className={`w-full text-left px-4 py-2 hover:bg-slate-700 transition-colors flex items-center justify-between ${
                      currentSymbol === item.symbol ? 'bg-teal-500/20 text-teal-400' : ''
                    }`}
                  >
                    <span className="font-medium">{item.symbol.split('.')[0]}</span>
                    <span className="text-xs text-slate-400">{item.name}</span>
                  </button>
                ))}
                <div className="p-2 border-y border-slate-700">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Crypto</p>
                </div>
                {popularSymbols.filter(s => s.symbol.endsWith('.CC')).map((item) => (
                  <button
                    key={item.symbol}
                    onClick={() => handleSelectSymbol(item.symbol)}
                    className={`w-full text-left px-4 py-2 hover:bg-slate-700 transition-colors flex items-center justify-between ${
                      currentSymbol === item.symbol ? 'bg-teal-500/20 text-teal-400' : ''
                    }`}
                  >
                    <span className="font-medium">{item.symbol.split('-')[0]}</span>
                    <span className="text-xs text-slate-400">{item.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Manual search input */}
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchSymbol}
              onChange={(e) => setSearchSymbol(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter symbol (e.g., AAPL.US)"
              className="bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2 w-full sm:w-64 text-sm focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
          >
            Analyze
          </button>
        </div>
      </div>

      {/* Symbol Info */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-800 rounded-xl border border-slate-700/50 p-4">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">{currentSymbol.split('.')[0]}</h2>
              {livePrice.isLive && (
                <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  LIVE
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">{currentSymbol}</p>
          </div>
          <div className="h-10 w-px bg-slate-700" />
          <div>
            <p className="text-2xl font-bold font-mono">
              {getCurrencySymbol(currentSymbol)}
              {(livePrice.isLive && livePrice.price ? livePrice.price : priceData.price).toFixed(2)}
            </p>
            <PriceChange value={
              livePrice.isLive && livePrice.price && priceData.previousClose
                ? ((livePrice.price - priceData.previousClose) / priceData.previousClose) * 100
                : priceData.change
            } size="sm" />
          </div>
        </div>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chart */}
        <div className="lg:col-span-3">
          <Card title="Price Chart">
            {bankerPrefs?.chart_provider === 'tradingview' ? (
              <TradingViewAdvancedChart symbol={currentSymbol} height={400} />
            ) : (
              <PriceChart symbol={currentSymbol} height={400} />
            )}
          </Card>
        </div>

        {/* Technical Indicators */}
        <div className="space-y-6">
          <Card title="Technical Indicators">
            {indicatorsLoading ? (
              <div className="py-8 flex items-center justify-center">
                <LoadingSpinner />
                <span className="ml-2 text-sm text-slate-400">Loading indicators...</span>
              </div>
            ) : technicalIndicators.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <p>No indicator data available</p>
              </div>
            ) : (
              <div className="space-y-2">
                {technicalIndicators.map((indicator, i) => (
                  <div key={i} className="group relative">
                    <div className="flex items-center justify-between py-2 px-2 -mx-2 border-b border-slate-700/50 last:border-0 rounded hover:bg-slate-700/30 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-300">{indicator.name}</span>
                        {indicator.description && (
                          <Info size={14} className="text-slate-500 cursor-help" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{indicator.value.toFixed(2)}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${getSignalBg(indicator.signal)} ${getSignalColor(indicator.signal)}`}
                        >
                          {indicator.signal.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {indicator.description && (
                      <div className="absolute left-0 right-0 top-full z-20 hidden group-hover:block">
                        <div className="mt-1 p-2 bg-slate-900 border border-slate-700 rounded text-xs text-slate-300 shadow-lg">
                          {indicator.description}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Signal Summary */}
          <Card title="Signal Summary">
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className={`text-3xl font-bold mb-2 ${
                  overallSignal === 'BULLISH' ? 'text-emerald-400' :
                  overallSignal === 'BEARISH' ? 'text-red-400' : 'text-slate-400'
                }`}>
                  {overallSignal}
                </div>
                <p className="text-sm text-slate-400">Based on {technicalIndicators.length} indicators</p>
                <div className="mt-2">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xs text-slate-500">Confidence:</span>
                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          overallSignal === 'BULLISH' ? 'bg-emerald-500' :
                          overallSignal === 'BEARISH' ? 'bg-red-500' : 'bg-slate-500'
                        }`}
                        style={{ width: `${confidenceScore}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono text-slate-400">{confidenceScore}%</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-500/20 rounded-lg p-3">
                  <TrendingUp size={20} className="mx-auto text-emerald-400 mb-1" />
                  <p className="text-lg font-bold text-emerald-400">
                    {technicalIndicators.filter((i) => i.signal === 'buy').length}
                  </p>
                  <p className="text-xs text-slate-400">Buy</p>
                </div>
                <div className="bg-slate-500/20 rounded-lg p-3">
                  <BarChart2 size={20} className="mx-auto text-slate-400 mb-1" />
                  <p className="text-lg font-bold text-slate-400">
                    {technicalIndicators.filter((i) => i.signal === 'neutral').length}
                  </p>
                  <p className="text-xs text-slate-400">Neutral</p>
                </div>
                <div className="bg-red-500/20 rounded-lg p-3">
                  <TrendingDown size={20} className="mx-auto text-red-400 mb-1" />
                  <p className="text-lg font-bold text-red-400">
                    {technicalIndicators.filter((i) => i.signal === 'sell').length}
                  </p>
                  <p className="text-xs text-slate-400">Sell</p>
                </div>
              </div>
            </div>
          </Card>

          {/* AI Analysis */}
          <AIAnalysis symbol={currentSymbol} />
        </div>
      </div>
    </div>
  );
};

export default Analysis;
